// M87 (2026-04-26): Admin sắp xếp thứ tự topic xuất hiện cho NV
// trong 3 bộ thưởng (flashcard learn).
//
// Route: /admin/flashcard-order
// 3 tab cho 3 bộ. Save → PUT /admin/flashcard/topic-order { bo, ordered_topics }.
//
// M325 (2026-05-30): UX nâng cấp — chọn nhiều topic (checkbox) + di chuyển hàng loạt
// (lên đầu/xuống cuối/lên 1/xuống 1) + kéo thả rõ ràng (vạch chèn + số thứ tự).
// Kéo 1 topic đang được chọn → kéo cả nhóm đang chọn.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';

const BO_TABS = [
  { key: 'bo_1000k',    label: 'Bộ 500k',      icon: '💰' },
  { key: 'bo_1250k',    label: 'Bộ 1.000k',    icon: '💎' },
  { key: 'bo_nang_cao', label: 'Bộ 1.250k',    icon: '🏆' },
];

export default function AdminFlashcardOrderPage() {
  const user = useAuthStore(s => s.user);
  const [activeBo, setActiveBo] = useState('bo_1000k');
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState(() => new Set());   // topic keys đang chọn
  // Drag state
  const [draggedKey, setDraggedKey] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);            // vị trí chèn 0..length

  if (!hasPermission(user, 'flashcard_order')) {
    return <div className="p-6 text-rose-600">403 — bạn không có quyền xem trang này.</div>;
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setDirty(false);
      setSelected(new Set());
      try {
        const r = await api.adminFlashcardTopicOrderGet(activeBo);
        if (!cancelled) setTopics(r?.topics || []);
      } catch (e) {
        if (!cancelled) toast.error('Lỗi load: ' + (e?.data?.error || e?.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeBo]);

  function apply(newArr) { setTopics(newArr); setDirty(true); }

  // ---- Chọn nhiều ----
  function toggleSel(key) {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  }
  const allSelected = topics.length > 0 && selected.size === topics.length;
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(topics.map(t => t.topic)));
  }

  // ---- Di chuyển 1 topic (nút ▲▼) ----
  function moveOne(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= topics.length) return;
    const a = [...topics];
    [a[idx], a[j]] = [a[j], a[idx]];
    apply(a);
  }

  // ---- Di chuyển hàng loạt (các topic đang chọn) ----
  function moveSelTop() {
    const sel = topics.filter(t => selected.has(t.topic));
    const rest = topics.filter(t => !selected.has(t.topic));
    apply([...sel, ...rest]);
  }
  function moveSelBottom() {
    const sel = topics.filter(t => selected.has(t.topic));
    const rest = topics.filter(t => !selected.has(t.topic));
    apply([...rest, ...sel]);
  }
  function moveSelUp() {
    const a = [...topics];
    for (let i = 1; i < a.length; i++) {
      if (selected.has(a[i].topic) && !selected.has(a[i - 1].topic)) {
        [a[i - 1], a[i]] = [a[i], a[i - 1]];
      }
    }
    apply(a);
  }
  function moveSelDown() {
    const a = [...topics];
    for (let i = a.length - 2; i >= 0; i--) {
      if (selected.has(a[i].topic) && !selected.has(a[i + 1].topic)) {
        [a[i], a[i + 1]] = [a[i + 1], a[i]];
      }
    }
    apply(a);
  }

  // ---- Kéo thả (1 topic hoặc cả nhóm đang chọn) ----
  function moveItemsTo(movingKeys, insertIdx) {
    const ms = new Set(movingKeys);
    const moving = topics.filter(t => ms.has(t.topic));
    const rest = topics.filter(t => !ms.has(t.topic));
    let restPos = 0;
    for (let i = 0; i < insertIdx; i++) if (!ms.has(topics[i].topic)) restPos++;
    rest.splice(restPos, 0, ...moving);
    apply(rest);
  }
  function onDragStart(e, idx) {
    setDraggedKey(topics[idx].topic);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }
  function onDragOverRow(e, idx) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > rect.top + rect.height / 2;
    const pos = after ? idx + 1 : idx;
    if (pos !== dropIndex) setDropIndex(pos);
  }
  function onDropList(e) {
    e.preventDefault();
    if (draggedKey == null || dropIndex == null) { resetDrag(); return; }
    const movingKeys = (selected.has(draggedKey) && selected.size > 1)
      ? topics.filter(t => selected.has(t.topic)).map(t => t.topic)
      : [draggedKey];
    moveItemsTo(movingKeys, dropIndex);
    resetDrag();
  }
  function resetDrag() { setDraggedKey(null); setDropIndex(null); }

  async function handleSave() {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      await api.adminFlashcardTopicOrderSet(activeBo, topics.map(t => t.topic));
      toast.success('✅ Đã lưu thứ tự');
      setDirty(false);
    } catch (e) {
      toast.error('Lỗi lưu: ' + (e?.data?.error || e?.message));
    } finally {
      setSaving(false);
    }
  }

  function switchTab(boKey) {
    if (dirty && !window.confirm('Có thay đổi chưa lưu — đổi tab sẽ mất. Tiếp tục?')) return;
    setActiveBo(boKey);
  }

  const selCount = selected.size;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
          <h1 className="text-lg font-semibold">📋 Thứ tự học theo bộ</h1>
          <span className="text-xs text-slate-400">Sắp xếp topic NV sẽ học trước/sau (flashcard 3 bộ thưởng)</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Tabs 3 bộ */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {BO_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`px-4 py-2 rounded-xl font-semibold text-sm transition border-2 ${
                activeBo === t.key
                  ? 'bg-sky-100 border-sky-300 text-sky-800 shadow-sm'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          {/* Thanh trên: số topic + lưu */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm text-slate-700">
              {loading ? 'Đang tải...' : (
                <>Có <strong>{topics.length}</strong> topic — tick chọn nhiều rồi bấm di chuyển, hoặc kéo thả ⋮⋮</>
              )}
            </div>
            <div className="flex items-center gap-2">
              {dirty && <span className="text-xs text-amber-600 italic">⚠ Chưa lưu</span>}
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Đang lưu...' : '💾 Lưu thứ tự'}
              </button>
            </div>
          </div>

          {/* Thanh chọn nhiều + di chuyển hàng loạt */}
          {!loading && topics.length > 0 && (
            <div className="flex items-center gap-2 mb-3 flex-wrap p-2 rounded-xl bg-slate-50 border border-slate-200">
              <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none px-1">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                       className="w-4 h-4 accent-sky-600" />
                Chọn tất cả
              </label>
              <span className="text-sm text-slate-400">|</span>
              {selCount > 0 ? (
                <>
                  <span className="text-sm font-semibold text-sky-700">Đã chọn {selCount}</span>
                  <button onClick={moveSelTop} className="px-2.5 py-1 text-xs rounded-lg bg-white border border-slate-300 hover:border-sky-400 hover:bg-sky-50 font-medium">⤒ Lên đầu</button>
                  <button onClick={moveSelUp} className="px-2.5 py-1 text-xs rounded-lg bg-white border border-slate-300 hover:border-sky-400 hover:bg-sky-50 font-medium">↑ Lên 1</button>
                  <button onClick={moveSelDown} className="px-2.5 py-1 text-xs rounded-lg bg-white border border-slate-300 hover:border-sky-400 hover:bg-sky-50 font-medium">↓ Xuống 1</button>
                  <button onClick={moveSelBottom} className="px-2.5 py-1 text-xs rounded-lg bg-white border border-slate-300 hover:border-sky-400 hover:bg-sky-50 font-medium">⤓ Xuống cuối</button>
                  <button onClick={() => setSelected(new Set())} className="px-2.5 py-1 text-xs rounded-lg text-slate-500 hover:text-slate-800 underline">Bỏ chọn</button>
                </>
              ) : (
                <span className="text-xs text-slate-400 italic">Tick vào ô vuông để chọn 1 hay nhiều topic, rồi dùng các nút di chuyển hàng loạt.</span>
              )}
            </div>
          )}

          {!loading && topics.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-xl">
              Bộ này chưa có câu hỏi nào (status='active'). Vào /admin/questions để thêm câu trước.
            </div>
          )}

          {/* Danh sách */}
          <div className="space-y-1" onDragOver={(e) => e.preventDefault()} onDrop={onDropList}>
            {topics.map((t, i) => {
              const isSel = selected.has(t.topic);
              const isDragged = draggedKey === t.topic;
              const showLineAbove = draggedKey != null && dropIndex === i;
              return (
                <div key={t.topic}>
                  {/* Vạch chèn (drop indicator) */}
                  <div className={showLineAbove ? 'h-0.5 bg-emerald-500 rounded my-1' : 'h-0.5 my-1'} />
                  <div
                    draggable
                    onDragStart={(e) => onDragStart(e, i)}
                    onDragOver={(e) => onDragOverRow(e, i)}
                    onDragEnd={resetDrag}
                    className={`flex items-center gap-3 p-3 border rounded-xl select-none transition-colors ${
                      isDragged ? 'opacity-40 bg-sky-100 border-sky-400' :
                      isSel ? 'bg-sky-50 border-sky-300' :
                      'bg-white border-slate-200 hover:border-sky-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleSel(t.topic)}
                      className="w-4 h-4 accent-sky-600 flex-shrink-0"
                      title="Chọn topic này"
                    />
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 text-xs font-bold flex-shrink-0 tabular-nums">{i + 1}</span>
                    <span className="text-slate-400 text-xl flex-shrink-0 cursor-grab active:cursor-grabbing" title="Kéo để sắp xếp">⋮⋮</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{t.topic || '(không topic)'}</div>
                      <div className="text-xs text-slate-500">{t.total_questions} câu hỏi</div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => moveOne(i, -1)} disabled={i === 0} title="Lên"
                        className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:border-sky-400 hover:bg-sky-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-700 font-bold">▲</button>
                      <button onClick={() => moveOne(i, +1)} disabled={i === topics.length - 1} title="Xuống"
                        className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:border-sky-400 hover:bg-sky-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-700 font-bold">▼</button>
                    </div>
                  </div>
                  {/* Vạch chèn cuối danh sách */}
                  {i === topics.length - 1 && (
                    <div className={draggedKey != null && dropIndex === topics.length ? 'h-0.5 bg-emerald-500 rounded my-1' : 'h-0.5 my-1'} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 <strong>Lưu ý:</strong> Thứ tự này áp dụng cho NV khi họ học flashcard mới (chưa từng gặp).
          Câu đã ở state ôn (review) thì SM-2 quyết định lúc nào ôn lại — không bị ảnh hưởng bởi thứ tự này.
        </div>
      </main>
    </div>
  );
}
